import Resource, { createResourceHook } from '@tabeo/resync'
import api from '@tabeo/sharpei/utils/api'
import formatSubresources from '@tabeo/sharpei/utils/formatSubresources'
import { useEffect } from 'react'
import { useMatch } from 'react-router'
import EventBus from 'utils/EventBus'
import normalize, { normalizeEntityLinks } from './normalizers/ticket'
import ticketSubResources from './Ticket/subResources'

function mergeWithLocal(local, remote) {
  return remote.map(remoteItem => {
    const localItem = local?.find(l => remoteItem.id === l.id)
    if (
      localItem &&
      new Date(localItem.updatedAt) > new Date(remoteItem.updatedAt)
    ) {
      return localItem
    }
    return remoteItem
  })
}

export const ticketsEvents = new EventBus()

class Tickets extends Resource {
  getInitialData() {
    return {
      items: null,
      totalCount: 0,
    }
  }

  getInitialConfig() {
    return {
      refreshInterval: 60 * 1000,
    }
  }

  async fetch(query) {
    const {
      searchQuery = query,
      limit = 200,
      offset = 0,
      conditions = [],
      virtualConditions = {},
      orderBy = {},
    } = this.params
    const { field = 'overdueAt', direction = 'asc' } = orderBy
    const jsonConditions = `&jsonConditions=${encodeURIComponent(
      JSON.stringify(conditions)
    )}&jsonVirtualConditions=${JSON.stringify(virtualConditions)}`
    const pagination = `&limit=${limit}&offset=${offset}`
    const endpoint = searchQuery
      ? `/ticket-search?order=${field}:${direction}&searchPhrase=${encodeURIComponent(
          searchQuery
        )}`
      : `/tickets?order=${field}:${direction}`

    const subResources = `&${formatSubresources(['owners', 'uploads'])}`

    const {
      data: { tickets },
      totalCount,
    } = await api.get(
      `${endpoint}${jsonConditions}${pagination}${subResources}`,
      {
        responseDataPath: 'data',
      }
    )

    const newItems = mergeWithLocal(this.data.items, tickets)
    const normalizedItems = newItems.map(t => normalize(t))

    const data = {
      totalCount,
      items: normalizedItems,
    }

    this.data = data

    return data
  }

  static create = async data => {
    const payload = {
      ...data,
      countryCodes: [
        ...new Set(
          data.entities?.map(e => e.data?.countryCode).filter(Boolean) || []
        ),
      ],
    }
    return api.post(`/tickets`, payload)
  }

  remove = async id => {
    this.data = {
      ...this.data,
      items: this.data.items.filter(item => item.id !== id),
    }
    const response = await api.delete(`/tickets/${id}`, {})
    this.fetch()
    return response
  }

  update = async (id, payload) => {
    const lastData = this.data

    // Create ticket for optimistic update
    const ticket = {
      ...payload,
      updatedAt: new Date(),
    }
    // Add new ticket to collection in cache
    const updatedItems = mergeWithLocal([ticket], this.data.items)
    this.data = {
      ...this.data,
      items: updatedItems,
    }

    try {
      // Get all relevant subresources for the update operation
      const { ticket: ticketWithSubresources } = await api.get(
        `/tickets/${id}?${ticketSubResources}`
      )

      const update = normalizeEntityLinks(
        normalize({ ...ticketWithSubresources, ...ticket })
      )

      // Initiate api call
      const response = await api.put(`/tickets/${id}`, update)
      await this.fetch()
      return response
    } catch (e) {
      // Set the error and revert optimistic values
      this.data = { ...lastData, localUpdateError: e }
    }
  }
}

export default Tickets
export const useResource = createResourceHook(Tickets)
const entityTypes = {
  offerId: 'offers',
  leadId: 'leads',
  billId: 'bills',
  consumerId: 'consumers',
  merchantId: 'merchants',
}
export function useTickets(params, config) {
  const transactionMatch = useMatch('/transactions/:offerId/*')
  const leadMatch = useMatch('/leads/:leadId/*')
  const billMatch = useMatch('/bills/:billId/*')
  const collectionAccountMatch = useMatch('/collection-accounts/:consumerId/*')
  const merchantMatch = useMatch('/merchants/:merchantId/*')
  const match =
    transactionMatch ??
    leadMatch ??
    billMatch ??
    collectionAccountMatch ??
    merchantMatch

  let smartParams
  if (!params && match?.params) {
    const entities = Object.keys(match.params)
      .map(key => {
        const type = entityTypes?.[key]
        return type ? { type, id: +match.params[key] } : false
      })
      .filter(Boolean)
    if (entities.length) {
      smartParams = {
        conditions: [
          {
            entities,
          },
        ],
      }
    }
  }

  const { data, resource } = useResource(params || smartParams, config)

  useEffect(() => {
    const unsubUpdate = ticketsEvents.on('update', () => {
      resource?.fetch?.()
    })
    const unsubRemove = ticketsEvents.on('remove', removedTicket => {
      if (resource?.data?.items) {
        resource.data = {
          ...resource.data,
          items: resource.data.items?.filter(
            ticket => ticket.id !== removedTicket.id
          ),
        }
      }
      resource?.fetch?.()
    })
    return () => {
      unsubUpdate()
      unsubRemove()
    }
  }, [resource])

  return { data, resource }
}
